import React, { useState, useCallback } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Button,
  ActionIcon,
  Badge,
  Tooltip,
  Alert,
  Modal,
  TextInput,
  NumberInput,
  Switch,
  Divider,
  Table
} from '@mantine/core';
import {
  IconPlus,
  IconMapPin,
  IconEdit,
  IconTrash,
  IconToggleLeft,
  IconToggleRight,
  IconAlertTriangle,
  IconInfoCircle
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { SubAreasManagerProps, SubAreaModalState, SubAreaFormData } from './SubAreasManager.types';
import { SubAreaDto } from '@/domain/entities/SubArea';
import {
  useSubAreasByRegionQuery,
  useCreateSubAreaMutation,
  useUpdateSubAreaMutation,
  useDeleteSubAreaMutation,
  useToggleSubAreaActiveMutation
} from '@/application/queries/subAreaQueries';
import { CoordinateInput } from '../CoordinateInput';
import { formatCoordinates } from '@/domain/value-objects/Coordinates';

/**
 * SubAreasManager Component
 *
 * Manages SubAreas for a Region with full CRUD operations
 *
 * Features:
 * - List all SubAreas for a Region
 * - Create new SubArea
 * - Edit existing SubArea
 * - Delete SubArea (with "last SubArea" validation)
 * - Toggle SubArea active status
 * - Admin-only access
 *
 * Usage:
 * ```tsx
 * <SubAreasManager
 *   regionId="seattle"
 *   userRole={currentUser.role}
 *   onSubAreaChange={() => refetchRegions()}
 * />
 * ```
 */
export const SubAreasManager: React.FC<SubAreasManagerProps> = ({
  regionId,
  userRole,
  readOnly = false,
  onSubAreaChange
}): React.ReactElement => {
  // Query: Fetch SubAreas
  const { data: subAreas = [], isLoading, error } = useSubAreasByRegionQuery(regionId, userRole);

  // Mutations
  const createMutation = useCreateSubAreaMutation();
  const updateMutation = useUpdateSubAreaMutation();
  const deleteMutation = useDeleteSubAreaMutation();
  const toggleActiveMutation = useToggleSubAreaActiveMutation();

  // Modal state
  const [modalState, setModalState] = useState<SubAreaModalState>({ type: null });

  // Form state
  const form = useForm<SubAreaFormData>({
    initialValues: {
      name: '',
      latitude: 0,
      longitude: 0,
      radiusKm: 5.0,
      isActive: true
    },
    validate: {
      name: (value) => (value.trim().length === 0 ? 'Name is required' : value.length > 200 ? 'Name must be 200 characters or less' : null),
      latitude: (value) => (value < -90 || value > 90 ? 'Latitude must be between -90 and 90' : null),
      longitude: (value) => (value < -180 || value > 180 ? 'Longitude must be between -180 and 180' : null),
      radiusKm: (value) => (value <= 0 ? 'Radius must be greater than 0' : value > 100 ? 'Radius must be 100km or less' : null)
    }
  });

  // Open create modal
  const handleCreateClick = useCallback((): void => {
    form.reset();
    form.setValues({
      name: '',
      latitude: 0,
      longitude: 0,
      radiusKm: 5.0,
      isActive: true
    });
    setModalState({ type: 'create' });
  }, [form]);

  // Open edit modal
  const handleEditClick = useCallback(
    (subArea: SubAreaDto): void => {
      form.setValues({
        name: subArea.name,
        latitude: subArea.latitude,
        longitude: subArea.longitude,
        radiusKm: subArea.radiusKm,
        isActive: subArea.isActive
      });
      setModalState({ type: 'edit', subArea });
    },
    [form]
  );

  // Open delete modal
  const handleDeleteClick = useCallback((subArea: SubAreaDto): void => {
    setModalState({ type: 'delete', subArea });
  }, []);

  // Close modal
  const handleCloseModal = useCallback((): void => {
    setModalState({ type: null });
    form.reset();
  }, [form]);

  // Handle create submit
  const handleCreateSubmit = useCallback(
    async (values: SubAreaFormData): Promise<void> => {
      await createMutation.mutateAsync({
        dto: {
          regionId,
          name: values.name.trim(),
          latitude: values.latitude,
          longitude: values.longitude,
          radiusKm: values.radiusKm,
          isActive: values.isActive
        },
        userRole
      });
      handleCloseModal();
      onSubAreaChange?.();
    },
    [createMutation, regionId, userRole, handleCloseModal, onSubAreaChange]
  );

  // Handle update submit
  const handleUpdateSubmit = useCallback(
    async (values: SubAreaFormData): Promise<void> => {
      if (!modalState.subArea) {return;}

      await updateMutation.mutateAsync({
        subAreaId: modalState.subArea.id,
        dto: {
          name: values.name.trim(),
          latitude: values.latitude,
          longitude: values.longitude,
          radiusKm: values.radiusKm,
          isActive: values.isActive
        },
        userRole
      });
      handleCloseModal();
      onSubAreaChange?.();
    },
    [updateMutation, modalState.subArea, userRole, handleCloseModal, onSubAreaChange]
  );

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!modalState.subArea) {return;}

    await deleteMutation.mutateAsync({
      subAreaId: modalState.subArea.id,
      regionId,
      userRole
    });
    handleCloseModal();
    onSubAreaChange?.();
  }, [deleteMutation, modalState.subArea, regionId, userRole, handleCloseModal, onSubAreaChange]);

  // Handle toggle active
  const handleToggleActive = useCallback(
    async (subArea: SubAreaDto): Promise<void> => {
      await toggleActiveMutation.mutateAsync({
        subAreaId: subArea.id,
        regionId,
        userRole
      });
      onSubAreaChange?.();
    },
    [toggleActiveMutation, regionId, userRole, onSubAreaChange]
  );

  // Check if attempting to delete last SubArea
  const isLastSubArea = subAreas.length === 1;

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Text size="lg" fw={600}>
            SubAreas
          </Text>
          <Text size="sm" c="dimmed">
            Manage geographic areas within this region
          </Text>
        </div>

        {!readOnly && userRole === 'Admin' && (
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateClick}>
            Add SubArea
          </Button>
        )}
      </Group>

      {/* Info Alert */}
      <Alert icon={<IconInfoCircle />} title="About SubAreas" color="blue" variant="light">
        SubAreas define specific geographic areas with coordinates and radius. Each Region must have at least one
        SubArea for business imports.
      </Alert>

      {/* Loading/Error States */}
      {isLoading && <Text c="dimmed">Loading SubAreas...</Text>}
      {error && (
        <Alert icon={<IconAlertTriangle />} title="Error Loading SubAreas" color="red">
          {error.message}
        </Alert>
      )}

      {/* SubAreas Table */}
      {!isLoading && !error && subAreas.length > 0 && (
        <Card withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Coordinates</Table.Th>
                <Table.Th>Radius</Table.Th>
                <Table.Th>Status</Table.Th>
                {!readOnly && userRole === 'Admin' && <Table.Th>Actions</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {subAreas.map((subArea) => (
                <Table.Tr key={subArea.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <IconMapPin size={16} />
                      <Text fw={500}>{subArea.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {formatCoordinates({ latitude: subArea.latitude, longitude: subArea.longitude })}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{subArea.radiusKm}km</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={subArea.isActive ? 'green' : 'gray'} variant="light">
                      {subArea.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  {!readOnly && userRole === 'Admin' && (
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label={subArea.isActive ? 'Deactivate' : 'Activate'}>
                          <ActionIcon
                            variant="light"
                            color={subArea.isActive ? 'orange' : 'green'}
                            onClick={() => handleToggleActive(subArea)}
                          >
                            {subArea.isActive ? <IconToggleRight size={18} /> : <IconToggleLeft size={18} />}
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Edit">
                          <ActionIcon variant="light" color="blue" onClick={() => handleEditClick(subArea)}>
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label={isLastSubArea ? 'Cannot delete last SubArea' : 'Delete'}>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDeleteClick(subArea)}
                            disabled={isLastSubArea}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && subAreas.length === 0 && (
        <Alert icon={<IconAlertTriangle />} title="No SubAreas" color="yellow">
          This region has no SubAreas yet. Add at least one SubArea to enable business imports.
        </Alert>
      )}

      {/* Create/Edit Modal */}
      <Modal
        opened={modalState.type === 'create' || modalState.type === 'edit'}
        onClose={handleCloseModal}
        title={modalState.type === 'create' ? 'Create SubArea' : 'Edit SubArea'}
        size="lg"
      >
        <form
          onSubmit={form.onSubmit((values) =>
            modalState.type === 'create' ? handleCreateSubmit(values) : handleUpdateSubmit(values)
          )}
        >
          <Stack gap="md">
            <TextInput label="Name" placeholder="Downtown Seattle" required {...form.getInputProps('name')} />

            <CoordinateInput
              label="Coordinates"
              description="Center point for this SubArea"
              value={{
                latitude: form.values['latitude'],
                longitude: form.values['longitude']
              }}
              onChange={(coords) => {
                form.setFieldValue('latitude', coords['latitude']);
                form.setFieldValue('longitude', coords['longitude']);
              }}
              required
              latitudeError={form.errors['latitude'] as string | undefined}
              longitudeError={form.errors['longitude'] as string | undefined}
            />

            <NumberInput
              label="Radius (km)"
              description="Search radius for OpenStreetMap imports"
              placeholder="5.0"
              min={0.1}
              max={100}
              decimalScale={1}
              step={0.5}
              required
              {...form.getInputProps('radiusKm')}
            />

            <Switch
              label="Active"
              description="Only active SubAreas are used for imports"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />

            <Divider />

            <Group justify="flex-end">
              <Button variant="light" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={modalState.type === 'create' ? createMutation.isPending : updateMutation.isPending}
              >
                {modalState.type === 'create' ? 'Create' : 'Save'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={modalState.type === 'delete'}
        onClose={handleCloseModal}
        title="Delete SubArea"
        size="md"
      >
        <Stack gap="md">
          {isLastSubArea && (
            <>
              <Alert icon={<IconAlertTriangle />} title="Cannot Delete Last SubArea" color="red" variant="filled">
                <Text size="sm" mt="xs">
                  This is the only SubArea in the region. Each region must have at least one SubArea to enable:
                </Text>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>Business imports from OpenStreetMap</li>
                  <li>Geographic mention tracking</li>
                  <li>Location-based Reddit monitoring</li>
                </ul>
              </Alert>

              <Text size="sm" c="dimmed" ta="center">
                To delete this SubArea, first create another SubArea for this region.
              </Text>

              <Divider />

              <Group justify="flex-end">
                <Button variant="filled" onClick={handleCloseModal}>
                  Understood
                </Button>
              </Group>
            </>
          )}

          {!isLastSubArea && (
            <>
              <Text>
                Are you sure you want to delete <strong>{modalState.subArea?.name}</strong>?
              </Text>

              <Alert icon={<IconInfoCircle />} color="blue" variant="light">
                Businesses associated with this SubArea will remain in the region but may lose their SubArea association.
              </Alert>

              <Divider />

              <Group justify="flex-end">
                <Button variant="light" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button color="red" onClick={handleDeleteConfirm} loading={deleteMutation.isPending}>
                  Delete SubArea
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
};
